import { supabase } from './supabase';

// ── Types ───────────────────────────────────────────────────

export type ActivityRiskLevel = 'low' | 'medium' | 'high' | 'critical';

export interface RiskScoreBreakdown {
  activity_points: number;
  safety_event_points: number;
  workforce_points: number;
  permit_points: number;
  total: number;
  signals: string[];
}

export interface RiskScoreResponse {
  dsrId: string;
  score: number;
  level: ActivityRiskLevel;
  computedLevel: ActivityRiskLevel;
  breakdown: RiskScoreBreakdown;
  previousLevel: ActivityRiskLevel | null;
  overridden: boolean;
}

// ── Client ──────────────────────────────────────────────────

// Fire-and-forget per-DSR risk scoring. Called after DSR submit
// and optionally on demand. See ARCH-030.
export async function triggerRiskScore(params: {
  dsrId: string;
  tenantId: string;
}): Promise<void> {
  try {
    await supabase.functions.invoke('risk-score', { body: params });
  } catch (err) {
    console.error('[risk-score] invoke failed', err);
  }
}

// Blocking call used by the manual "Recalculate" button in the
// detail view. Returns the stored response so the UI can update
// its cache without a refetch.
export async function computeRiskScore(params: {
  dsrId: string;
  tenantId: string;
}): Promise<RiskScoreResponse | null> {
  const { data, error } = await supabase.functions.invoke<RiskScoreResponse>(
    'risk-score',
    { body: params },
  );
  if (error) {
    console.error('[risk-score] compute failed', error);
    return null;
  }
  return (data as RiskScoreResponse) ?? null;
}

// ── Override ────────────────────────────────────────────────

export async function setRiskLevelOverride(params: {
  dsrId: string;
  level: ActivityRiskLevel | null;
  reason: string | null;
  userId: string;
}): Promise<void> {
  const { dsrId, level, reason, userId } = params;

  // Clearing the override: set all three columns to null. The DB
  // check constraint requires the triad to move together.
  if (!level || !reason) {
    const { error } = await supabase
      .from('dsr_reports')
      .update({
        risk_level_override: null,
        risk_level_override_reason: null,
        risk_level_override_by: null,
      })
      .eq('id', dsrId);
    if (error) throw error;
    return;
  }

  const { error } = await supabase
    .from('dsr_reports')
    .update({
      risk_level_override: level,
      risk_level_override_reason: reason,
      risk_level_override_by: userId,
      // Reflect the override in overall_risk_level immediately so
      // the reports list / analytics reads without waiting for a
      // re-run of the Edge Function. The Edge Function will
      // overwrite it next time it fires, preserving the override.
      overall_risk_level: level,
    })
    .eq('id', dsrId);
  if (error) throw error;
}

// ── SBAR narrative (S75) ────────────────────────────────────

// Writes the Assessment + Recommendation free-text fields. Kept
// separate from setRiskLevelOverride: notes have no CHECK-enforced
// pairing with the override triad, and the form may save them
// independently (optional fields).
export async function setRiskSbarNotes(params: {
  dsrId: string;
  assessmentNote: string | null;
  recommendationNote: string | null;
}): Promise<void> {
  const { dsrId, assessmentNote, recommendationNote } = params;
  const { error } = await supabase
    .from('dsr_reports')
    .update({
      risk_assessment_note: assessmentNote,
      risk_recommendation_note: recommendationNote,
    })
    .eq('id', dsrId);
  if (error) throw error;
}
